import { notification } from 'antd';
import { AxiosResponse } from 'axios';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../api/useAPIMutation';
import { JobPostingDetail } from '../types';
import { buildCandidateQueryParam, buildRecruitmentProcessQueryParam } from '../utils';
import SelectCandidateModal, {
  SelectCandidateFormValues,
  SelectCandidateModalProps,
} from './SelectCandidateModal';

interface BulkSelectCandidateModalProps extends Omit<SelectCandidateModalProps, 'helmetTitle'> {
  actionOrigin: 'Banco de Talentos' | 'Triagem entre vagas';
  onSuccess?: (
    data: AxiosResponse<unknown, unknown> | undefined | void,
    variables: SelectCandidateFormValues
  ) => void | Promise<unknown>;
  totalCount?: number;
  jobPostingId?: JobPostingDetail['id'];
  functionId?: JobPostingDetail['function_id'];
}

export default function BulkSelectCandidateModal({
  actionOrigin,
  onSuccess,
  totalCount,
  jobPostingId,
  functionId,
  ...props
}: BulkSelectCandidateModalProps) {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();

  const sourceJobPosting = jobPostingId;

  const mutation = useAPIMutation(
    async ({ jobPostingId }: SelectCandidateFormValues, api) => {
      if (sourceJobPosting) {
        await api.post(
          '/recruitment-processes-actions/select-all/',
          {
            job_posting: jobPostingId,
          },
          {
            params: await buildRecruitmentProcessQueryParam(
              searchParams,
              api,
              sourceJobPosting,
              functionId
            ),
          }
        );
      } else {
        await api.post(
          '/candidates-actions/select-all/',
          {
            job_posting: jobPostingId,
          },
          {
            params: buildCandidateQueryParam(searchParams),
          }
        );
      }
    },
    {
      onSuccess,
      onSettled: async () => {
        await queryClient.invalidateQueries('useSearchCandidates');
        await queryClient.invalidateQueries('/candidate-history/');
        await queryClient.invalidateQueries(['questionnaireUserAnswers']);
        await queryClient.invalidateQueries('/recruitment-processes/');
      },
      onError: (err, variables, context) => {
        if (err?.response?.data?.error === 'empty_queryset_select_all') {
          return notification.error({
            duration: 0,
            message: t('Erro'),
            description: t(
              'Não é possível realizar esta ação, pois não há candidatos a serem selecionados. Recarregue a página e tente novamente.'
            ),
          });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
    }
  );

  return (
    <SelectCandidateModal
      loading={mutation.isLoading}
      title={
        <span>
          <Trans>Triar</Trans>{' '}
          <strong>
            {totalCount ?? '...'} <Trans>candidato(s)</Trans>
          </strong>
        </span>
      }
      onFinish={(variables) => {
        mutation.mutate(variables);
      }}
      helmetTitle={t(`Triar {{totalCount}} candidato(s)`, { totalCount: totalCount ?? '...' })}
      {...props}
    />
  );
}
